import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { ArrowRight, Phone, Star, Quote, CalendarDays, Megaphone, ArrowUpRight } from 'lucide-react';
import { SCHOOL, STATS, NOTICES, EVENTS, TESTIMONIALS, PRINCIPAL, GALLERY_ITEMS, IMG } from '@/data/content';
import { Reveal, MaskedLines, SectionHeading } from '@/components/Reveal';

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-navy text-cream noise-overlay">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img src="/images/hero-building.jpg" alt="Ideal Public School campus" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/40" />
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-24 pb-16 sm:pt-32 sm:pb-24">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-gold"
          data-testid="admission-badge"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-gold" />
          </span>
          Admissions Open · Session {SCHOOL.session}
        </motion.div>

        <MaskedLines
          delay={0.25}
          className="mt-8"
          lineClassName="font-display text-5xl sm:text-7xl lg:text-8xl font-black tracking-tight leading-[1.02]"
          lines={[
            'Where Education',
            <span key="l2">Meets <em className="text-gold not-italic font-display italic">Excellence.</em></span>,
          ]}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-cream/80 leading-relaxed"
        >
          {SCHOOL.tagline} A caring, disciplined and modern campus in the heart of {SCHOOL.place}.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link to="/admissions" data-testid="apply-now-btn" className="group inline-flex items-center gap-2 rounded-full bg-crimson px-7 py-3.5 text-sm font-bold text-white transition-all hover:bg-crimson-dark hover:-translate-y-0.5">
            Apply Now <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link to="/contact" data-testid="contact-us-btn" className="inline-flex items-center gap-2 rounded-full border border-cream/40 px-7 py-3.5 text-sm font-bold text-cream transition-all hover:border-gold hover:text-gold hover:-translate-y-0.5">
            Contact Us
          </Link>
          <a href={`tel:${SCHOOL.phones[0].replace(/\s/g, '')}`} data-testid="hero-call-link" className="inline-flex items-center gap-2 text-sm font-semibold text-cream/80 hover:text-gold transition-colors">
            <Phone className="h-4 w-4 text-gold" /> {SCHOOL.phones[0]}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-16 grid grid-cols-2 gap-6 border-t border-cream/15 pt-8 sm:grid-cols-4"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-display text-3xl sm:text-4xl font-bold text-gold">{s.value}</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.15em] text-cream/60">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const EditorialMarquee = () => (
  <div className="border-y border-navy/10 bg-cream-dark py-5 overflow-hidden" data-testid="editorial-marquee">
    <Marquee speed={45} gradient={false} pauseOnHover>
      {['Admissions Open 2026–27', 'Smart Classrooms', 'Experienced Faculty', 'Safe Transport', 'Value-Based Education', 'Holistic Development'].map((t) => (
        <span key={t} className="mx-8 flex items-center gap-8 font-display text-2xl sm:text-3xl font-bold italic text-navy/80">
          {t} <Star className="h-5 w-5 text-gold fill-gold" aria-hidden="true" />
        </span>
      ))}
    </Marquee>
  </div>
);

const AboutPreview = () => (
  <section className="py-24 sm:py-32" data-testid="about-preview">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-14 lg:grid-cols-12 items-center">
      <div className="lg:col-span-5 relative">
        <Reveal>
          <div className="relative">
            <div className="absolute -left-4 -top-4 h-full w-full rounded-lg border-2 border-gold" aria-hidden="true" />
            <img src="/images/campus-gate.jpg" alt="Students at the school gate" className="relative rounded-lg object-cover aspect-[4/5] w-full" />
            <img src={`${IMG.classroom}&w=600&q=75&auto=format&fit=crop`} alt="Classroom" className="absolute -bottom-10 -right-4 sm:-right-10 w-40 sm:w-52 rounded-lg border-4 border-cream object-cover aspect-square shadow-xl shadow-navy/20" />
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-7 lg:pl-10">
        <SectionHeading number="01" eyebrow="Who We Are" title="A school built on values, run with vision." />
        <Reveal delay={0.2}>
          <p className="mt-6 text-navy/70 leading-relaxed">
            For over two decades, Ideal Public School has been the first choice of families across Shahapur, Khetasarai and nearby villages.
            We combine the discipline of a traditional Indian school with smart classrooms, dedicated teachers and a campus where every child is safe, seen and inspired.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <ul className="mt-8 grid sm:grid-cols-2 gap-4">
            {['Quality Education', 'Experienced Faculty', 'Modern Facilities', 'Holistic Development'].map((f) => (
              <li key={f} className="flex items-center gap-3 rounded-lg border border-navy/10 bg-white px-4 py-3 text-sm font-semibold text-navy">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-crimson text-white shrink-0"><Star className="h-4 w-4 fill-white" /></span>
                {f}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.4}>
          <Link to="/about" data-testid="about-discover-link" className="group mt-9 inline-flex items-center gap-2 text-sm font-bold text-crimson">
            Discover our story <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const PrincipalSection = () => (
  <section className="bg-cream-dark py-24 sm:py-32" data-testid="principal-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-14 lg:grid-cols-12 items-center">
      <div className="lg:col-span-7 order-2 lg:order-1">
        <SectionHeading number="02" eyebrow="From the Principal's Desk" title="“Education is the most powerful weapon to change the world.”" />
        <Reveal delay={0.2}>
          <p className="mt-6 text-navy/70 leading-relaxed">{PRINCIPAL.message[1]}</p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-8 flex items-center gap-4">
            <div className="h-px w-12 bg-crimson" aria-hidden="true" />
            <div>
              <p className="font-display text-lg font-bold text-navy">{PRINCIPAL.name}</p>
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-navy/50">{PRINCIPAL.designation} · {PRINCIPAL.qualification}</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.4}>
          <Link to="/about" data-testid="principal-read-more" className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-crimson">
            Read the full message <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="lg:col-span-5 order-1 lg:order-2">
        <Reveal>
          <div className="relative mx-auto max-w-sm">
            <div className="absolute -right-4 -bottom-4 h-full w-full rounded-lg bg-crimson" aria-hidden="true" />
            <img src={PRINCIPAL.photo} alt="Principal of Ideal Public School" className="relative rounded-lg object-cover aspect-[4/5] w-full" />
            <div className="absolute bottom-4 left-4 rounded-md bg-navy/90 px-4 py-2 backdrop-blur">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold">Principal Office</p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const NoticesEvents = () => (
  <section className="py-24 sm:py-32" data-testid="notices-events">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionHeading number="03" eyebrow="Stay Updated" title="Notices & upcoming events" />
        <Reveal delay={0.2}>
          <Link to="/notices" data-testid="view-all-notices" className="group inline-flex items-center gap-2 rounded-full border border-navy/20 px-5 py-2.5 text-sm font-bold text-navy transition-all hover:border-crimson hover:text-crimson">
            View all <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-12 lg:grid-cols-2">
        <div>
          <Reveal>
            <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-crimson"><Megaphone className="h-4 w-4" /> Latest Announcements</h3>
          </Reveal>
          <div className="mt-6 divide-y divide-navy/10">
            {NOTICES.slice(0, 4).map((n, i) => (
              <Reveal key={n.title} delay={i * 0.08}>
                <Link to="/notices" data-testid={`home-notice-${i}`} className="group flex gap-5 py-5">
                  <span className="shrink-0 rounded-md bg-navy px-3 py-2 text-center">
                    <span className="block font-display text-lg font-bold text-gold leading-none">{n.date.split(' ')[0]}</span>
                    <span className="block text-[10px] font-bold uppercase tracking-widest text-cream/60">{n.date.split(' ')[1]}</span>
                  </span>
                  <span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-crimson">{n.tag}</span>
                    <span className="block font-semibold text-navy group-hover:text-crimson transition-colors">{n.title}</span>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
        <div>
          <Reveal>
            <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-crimson"><CalendarDays className="h-4 w-4" /> Upcoming Events</h3>
          </Reveal>
          <div className="mt-6 space-y-4">
            {EVENTS.slice(0, 4).map((e, i) => (
              <Reveal key={e.title} delay={i * 0.08}>
                <div data-testid={`home-event-${i}`} className="flex items-center justify-between gap-4 rounded-lg border border-navy/10 bg-white px-5 py-4 transition-all hover:-translate-y-0.5 hover:border-gold">
                  <div>
                    <p className="font-semibold text-navy">{e.title}</p>
                    <p className="text-xs text-navy/50 mt-0.5">{e.time} · {e.venue}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-gold-light px-3 py-1.5 text-xs font-bold text-navy">
                    {new Date(e.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const GalleryBento = () => {
  const items = GALLERY_ITEMS.slice(0, 5);
  const spanCls = { wide: 'md:col-span-2', tall: 'md:row-span-2', normal: '' };
  return (
    <section className="bg-navy py-24 sm:py-32 noise-overlay" data-testid="gallery-preview">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeading dark number="04" eyebrow="Campus Life" title="Moments that make Ideal, ideal." />
          <Reveal delay={0.2}>
            <Link to="/gallery" data-testid="view-gallery-link" className="group inline-flex items-center gap-2 rounded-full border border-cream/30 px-5 py-2.5 text-sm font-bold text-cream transition-all hover:border-gold hover:text-gold">
              Full gallery <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 auto-rows-[160px] sm:auto-rows-[200px] gap-4">
          {items.map((g, i) => (
            <Reveal key={g.caption + i} delay={i * 0.07} className={spanCls[g.span] || ''}>
              <Link to="/gallery" data-testid={`bento-item-${i}`} className="group relative block h-full w-full overflow-hidden rounded-lg">
                <img src={g.src} alt={g.caption} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <p className="absolute bottom-3 left-3 text-sm font-semibold text-cream opacity-0 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">{g.caption}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => (
  <section className="py-24 sm:py-32" data-testid="testimonials-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeading number="05" eyebrow="Voices of Trust" title="What parents & students say" />
      <div className="mt-14 grid gap-6 md:grid-cols-2">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.author} delay={i * 0.08}>
            <figure data-testid={`testimonial-${i}`} className="h-full rounded-lg border border-navy/10 bg-white p-8 transition-all hover:-translate-y-1 hover:border-gold">
              <Quote className="h-8 w-8 text-gold fill-gold" aria-hidden="true" />
              <blockquote className="mt-5 font-display text-lg italic leading-relaxed text-navy/85">“{t.quote}”</blockquote>
              <figcaption className="mt-6 flex items-center justify-between">
                <div>
                  <p className="font-bold text-navy">{t.author}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-navy/50">{t.role}</p>
                </div>
                <div className="flex gap-0.5" aria-label="5 star rating">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} className="h-3.5 w-3.5 text-gold fill-gold" />
                  ))}
                </div>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CtaBand = () => (
  <section className="relative overflow-hidden bg-crimson py-20 sm:py-24 noise-overlay" data-testid="cta-band">
    <div className="absolute -right-20 -bottom-24 h-72 w-72 rounded-full border-[28px] border-white/10" aria-hidden="true" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row lg:items-center gap-10 justify-between">
      <div className="max-w-xl">
        <Reveal>
          <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight text-white leading-[1.1]">
            Give your child the best start <span className="italic text-gold">for a brighter tomorrow.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-5 text-white/80">Limited seats for Session {SCHOOL.session}. Enquire today — our admissions team will call you back.</p>
        </Reveal>
      </div>
      <Reveal delay={0.25}>
        <div className="flex flex-wrap gap-4">
          <Link to="/admissions" data-testid="cta-apply-btn" className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-crimson transition-all hover:-translate-y-0.5 hover:bg-cream">
            Apply Now <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <a href={`tel:${SCHOOL.phones[0].replace(/\s/g, '')}`} data-testid="cta-call-btn" className="inline-flex items-center gap-2 rounded-full border border-white/50 px-7 py-3.5 text-sm font-bold text-white transition-all hover:border-gold hover:text-gold">
            <Phone className="h-4 w-4" /> {SCHOOL.phones[0]}
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <EditorialMarquee />
    <AboutPreview />
    <PrincipalSection />
    <NoticesEvents />
    <GalleryBento />
    <Testimonials />
    <CtaBand />
  </>
);

export default Home;
